import { Link } from 'react-router'

import { Skeleton } from '@/components/ui/skeleton'
import { VatPayerReliability } from '@/containers/Contacts/VatPayerReliability'
import { Participant } from '@/interfaces/Participant'
import { cn } from '@/lib/utils'

const degrees = ['doc.', 'prof.', 'phdr.', 'phd.', 'bc.', 'ing.', 'mgr.', 'arch.', 'et']

export const getInitials = (name: string) =>
  name
    .split(' ')
    .filter((part) => part && !degrees.includes(part.toLowerCase()))
    .map((n) => n[0])
    .join('')
    .substring(0, 2)
    .toUpperCase()

/** Kulatý avatar s iniciálami, monochromní (DESIGN.md: barva jen pro stavy). */
export const InitialsAvatar = ({ name, className }: { name?: string | null; className?: string }) => (
  <span
    aria-hidden
    className={cn(
      'inline-flex size-8 shrink-0 items-center justify-center rounded-full border bg-muted text-xs font-medium text-muted-foreground',
      className,
    )}
  >
    {getInitials(name || '') || '?'}
  </span>
)

export interface ParticipantProps {
  participant: Participant | undefined
  link?: boolean
  /** Odznak spolehlivosti plátce. Ukazujeme jen nespolehlivé plátce, ostatní stavy jsou šum. */
  showVatPayerReliability?: boolean
}

export const ParticipantAvatar = ({ participant, link, showVatPayerReliability = true }: ParticipantProps) => {
  if (!participant) {
    return (
      <div className="flex items-center gap-3">
        <Skeleton className="size-8 rounded-full" />
        <div className="space-y-1.5">
          <Skeleton className="h-4 w-32" />
          <Skeleton className="h-3 w-24" />
        </div>
      </div>
    )
  }

  const secondary = participant.email || participant.phone

  return (
    <div className="flex min-w-0 items-center gap-3">
      <InitialsAvatar name={participant.name} />
      <div className="min-w-0">
        <p className="truncate text-sm font-medium">
          {link ? (
            <Link to={`/contact/${participant.id}`} className="hover:underline">
              {participant.name}
            </Link>
          ) : (
            participant.name
          )}
        </p>
        {secondary && <p className="truncate text-xs text-muted-foreground">{secondary}</p>}
        {showVatPayerReliability &&
          participant.vatPayer === 'vatPayer' &&
          participant.vatPayerReliability === 'notReliable' && (
            <VatPayerReliability participant={participant} className="mt-1" />
          )}
      </div>
    </div>
  )
}

ParticipantAvatar.displayName = 'ParticipantAvatar'
